import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { animate, svg } from 'animejs';

// ─── Silver/steel color system ───────────────────────────────────────────
const silver = '#B8C0CC';
const shimmerPeak = '#FFFFFF';

// ─── Castle structure paths ──────────────────────────────────────────────
const rookPathsAt = (dx: number) => [
  `M${14 + dx} 8 V2 H${22 + dx} V8 H${27 + dx} V2 H${37 + dx} V8 H${42 + dx} V2 H${50 + dx} V8`,
  `M${12 + dx} 8 H${52 + dx} V16 H${12 + dx} Z`,
  `M${16 + dx} 16 L${20 + dx} 30 H${44 + dx} L${48 + dx} 16`,
  `M${18 + dx} 30 H${46 + dx} V52 H${18 + dx} Z`,
  `M${18 + dx} 40 H${46 + dx}`,
  `M${18 + dx} 52 L${12 + dx} 60 H${52 + dx} L${46 + dx} 52`,
];

const BATTLEMENT_PATH = (() => {
  let d = `M46 30 L46 22`;
  for (let i = 0; i < 21; i++) {
    const x = 46 + i * 8;
    d += ` L${x} 22 L${x + 4} 22 L${x + 4} 30`;
  }
  return d;
})();

const PLAQUE_PATHS = [
  `M56 33 H200 V57 H56 Z`,
  `M58 35 H198 V55 H58 Z`,
  `M56 33 L53 30 M56 57 L53 60 M200 33 L203 30 M200 57 L203 60`,
];

const STRUCTURE = [
  ...rookPathsAt(0),
  ...rookPathsAt(192),
  BATTLEMENT_PATH,
  `M46 30 H210 V60 H46 Z`,
  `M46 40 H210`,
  `M46 50 H210`,
  `M8 60 H248 V68 H8 Z`,
  `M4 68 H252 V74 H4 Z`,
];

// ─── Pixel-art stone brick generator ────────────────────────────────────
// Builds staggered brick tiles for each stone region of the castle.
const brickW = 6;
const brickH = 3;
interface Brick { x: number; y: number; w: number; h: number; shade: number }
function generateBricks(regions: { x: number; y: number; width: number; height: number }[]): Brick[] {
  const bricks: Brick[] = [];
  regions.forEach((r) => {
    let rowIdx = 0;
    for (let by = r.y; by < r.y + r.height; by += brickH) {
      const stagger = rowIdx % 2 === 0 ? 0 : brickW / 2;
      for (let bx = r.x - stagger; bx < r.x + r.width; bx += brickW) {
        const clipX = Math.max(bx, r.x);
        const clipW = Math.min(bx + brickW, r.x + r.width) - clipX;
        const clipY = by;
        const clipH = Math.min(by + brickH, r.y + r.height) - clipY;
        if (clipW > 0.5 && clipH > 0.5) {
          // Deterministic pseudo-random shade per brick for texture variation
          const hash = Math.abs(Math.sin(clipX * 12.9898 + clipY * 78.233) * 43758.5453) % 1;
          bricks.push({ x: clipX, y: clipY, w: clipW, h: clipH, shade: hash });
        }
      }
      rowIdx++;
    }
  });
  return bricks;
}

// Regions of the castle that show stone brick texture
const BRICKS: Brick[] = generateBricks([
  // Left rook crown + body (y=8-52, x=12-52) — skip the battlement notches at top
  { x: 12, y: 8, width: 40, height: 8 },          // crown
  { x: 16, y: 16, width: 32, height: 14 },        // taper (approx)
  { x: 18, y: 30, width: 28, height: 22 },        // body
  // Right rook (same, offset 192)
  { x: 204, y: 8, width: 40, height: 8 },
  { x: 208, y: 16, width: 32, height: 14 },
  { x: 210, y: 30, width: 28, height: 22 },
  // Curtain wall body
  { x: 46, y: 30, width: 164, height: 30 },
  // Shared base (y=60-68) — full width across both rook bases
  { x: 8, y: 60, width: 240, height: 8 },
  // Foundation (y=68-74)
  { x: 4, y: 68, width: 248, height: 6 },
]);

// Arrow-slit windows on each rook tower (visible dark openings)
const WINDOWS = [
  { x: 30.5, y: 34, w: 3, h: 8 },   // left rook
  { x: 222.5, y: 34, w: 3, h: 8 },  // right rook
];

const ALL_TRACE_PATHS = [...STRUCTURE, ...PLAQUE_PATHS];

interface SiegeTitleProps {
  /** Unused — title is silver by design. Kept for API stability. */
  tierColor?: string;
}

export function SiegeTitle(_: SiegeTitleProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [phase, setPhase] = useState<'tracing' | 'done'>('tracing');
  const words = ['Strategically', 'Integrated', 'Equity', 'Growth', 'Engine'];
  const uid = useRef(`siege-${Math.random().toString(36).slice(2, 8)}`).current;
  const pulseId = `${uid}-pulse`;
  const glowFilterId = `${uid}-glow-filter`;

  useEffect(() => {
    if (!svgRef.current) return;
    const cutPaths = svgRef.current.querySelectorAll(`#${uid}-cutLayer path`);
    if (cutPaths.length === 0) return;
    const drawables = svg.createDrawable(cutPaths);
    animate(drawables, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2400,
      delay: (_el: any, i: number) => i * 50,
      onComplete: () => {
        const cut = svgRef.current?.querySelector(`#${uid}-cut`);
        if (cut) {
          animate(cut, {
            opacity: [1, 0],
            duration: 700,
            ease: 'outQuad',
            onComplete: () => setTimeout(() => setPhase('done'), 100),
          });
        }
      },
    });
  }, [uid]);

  return (
    <div className="text-center">
      <div className="relative flex items-center justify-center" style={{ width: 480, height: 150, margin: '0 auto' }}>
        {/* Castle with plaque — single unified SVG */}
        <svg ref={svgRef} viewBox="0 0 256 80" width="480" height="150"
          preserveAspectRatio="xMidYMid meet" className="absolute inset-0"
          shapeRendering="crispEdges">
          <defs>
            <radialGradient id={`${uid}-ambient`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={silver} stopOpacity="0.05" />
              <stop offset="100%" stopColor={silver} stopOpacity="0" />
            </radialGradient>

            <motion.linearGradient id={pulseId} gradientUnits="userSpaceOnUse"
              animate={{ x1: [-40, 296], x2: [-20, 316], y1: [-10, 90], y2: [0, 100] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 4, ease: 'linear' }}>
              <stop stopColor={silver} stopOpacity="0" />
              <stop offset="0.4" stopColor={silver} stopOpacity="1" />
              <stop offset="0.5" stopColor={shimmerPeak} stopOpacity="1" />
              <stop offset="0.6" stopColor={silver} stopOpacity="1" />
              <stop offset="1" stopColor={silver} stopOpacity="0" />
            </motion.linearGradient>

            <filter id={glowFilterId} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect width="256" height="80" fill={`url(#${uid}-ambient)`} />

          {/* Dark mortar base — renders behind bricks so gaps look like true mortar */}
          <g>
            {[
              { x: 12, y: 8, width: 40, height: 8 },
              { x: 16, y: 16, width: 32, height: 14 },
              { x: 18, y: 30, width: 28, height: 22 },
              { x: 204, y: 8, width: 40, height: 8 },
              { x: 208, y: 16, width: 32, height: 14 },
              { x: 210, y: 30, width: 28, height: 22 },
              { x: 46, y: 30, width: 164, height: 30 },
              { x: 8, y: 60, width: 240, height: 8 },
              { x: 4, y: 68, width: 248, height: 6 },
            ].map((r, i) => (
              <rect key={i} x={r.x} y={r.y} width={r.width} height={r.height} fill="#0a0d13" />
            ))}
          </g>

          {/* Pixel-art stone bricks — chunky, visible, with dark mortar borders */}
          <g>
            {BRICKS.map((b, i) => {
              const fillOp = 0.32 + b.shade * 0.18;  // 0.32-0.50
              return (
                <g key={i}>
                  {/* Main brick face */}
                  <rect
                    x={b.x + 0.4} y={b.y + 0.4}
                    width={b.w - 0.8} height={b.h - 0.8}
                    fill={silver} fillOpacity={fillOp}
                    stroke="#0a0d13" strokeWidth="0.4" strokeOpacity="1" />
                  {/* Top highlight — catches light */}
                  <rect
                    x={b.x + 0.4} y={b.y + 0.4}
                    width={b.w - 0.8} height={0.4}
                    fill={silver} fillOpacity={0.4} />
                  {/* Bottom shadow — depth */}
                  <rect
                    x={b.x + 0.4} y={b.y + b.h - 0.8}
                    width={b.w - 0.8} height={0.4}
                    fill="#000" fillOpacity={0.4} />
                </g>
              );
            })}
          </g>

          {/* Arrow-slit windows — deep black recess with bright silver frame */}
          <g>
            {WINDOWS.map((w, i) => (
              <g key={i}>
                <rect x={w.x - 0.5} y={w.y - 0.5} width={w.w + 1} height={w.h + 1} fill="#000" />
                <rect x={w.x} y={w.y} width={w.w} height={w.h} fill="#050810" />
                <rect x={w.x} y={w.y} width={w.w} height={w.h}
                  fill="none" stroke={silver} strokeWidth="0.5" opacity="0.85" />
                {/* Inner slit highlight — faint light inside */}
                <rect x={w.x + 0.5} y={w.y + 1} width={w.w - 1} height={0.3}
                  fill={silver} opacity="0.3" />
              </g>
            ))}
          </g>

          {/* Castle outer stroke — thick pixel outline (like the reference) */}
          <g stroke="#0a0d13" strokeWidth="2.5" strokeLinecap="butt" strokeLinejoin="miter"
            fill="none" opacity="0.9">
            {STRUCTURE.map((d, i) => <path key={i} d={d} />)}
          </g>
          <g stroke={silver} strokeWidth="1" strokeLinecap="butt" strokeLinejoin="miter"
            fill="none" opacity="0.85">
            {STRUCTURE.map((d, i) => <path key={i} d={d} />)}
          </g>

          {/* Opaque plaque backdrop — covers castle lines beneath the inscription */}
          <rect x="56" y="33" width="144" height="24" fill="#0d1117" />

          {/* Plaque frame */}
          <g stroke={silver} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
            fill="none" opacity="0.5">
            {PLAQUE_PATHS.map((d, i) => <path key={i} d={d} />)}
          </g>

          {/* Shimmer pulse overlay — glowing scan across everything */}
          {phase === 'done' && (
            <g filter={`url(#${glowFilterId})`}>
              <g strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"
                stroke={`url(#${pulseId})`}>
                {ALL_TRACE_PATHS.map((d, i) => <path key={i} d={d} />)}
              </g>
            </g>
          )}

          {/* Anime.js trace cover */}
          <g id={`${uid}-cut`}>
            <rect width="256" height="80" fill="#0d1117" opacity="0.98" />
            <g id={`${uid}-cutLayer`} stroke={silver} strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" fill="none">
              {ALL_TRACE_PATHS.map((d, i) => <path key={i} d={d} />)}
            </g>
          </g>
        </svg>

        {/* SIEGE title — positioned over the plaque */}
        <div className="relative z-10 flex items-center justify-center" style={{ marginTop: 18 }}>
          <motion.h1
            className="leading-none text-white"
            style={{
              fontFamily: "'Metamorphous', serif",
              fontSize: 34,
              fontWeight: 400,
              letterSpacing: '0.14em',
              textShadow: [
                `0 0 4px ${silver}`,
                `0 0 10px ${silver}`,
                `0 0 20px ${silver}CC`,
                `0 0 34px ${silver}88`,
                `0 2px 6px rgba(0,0,0,0.9)`,
              ].join(', '),
            }}
            initial={{ opacity: 0 }}
            animate={phase === 'done' ? { opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            SIEGE
          </motion.h1>
        </div>
      </div>

      {/* Descriptor below castle */}
      <div className="flex items-center justify-center gap-0 mt-2">
        {words.map((w, i) => (
          <motion.span key={i} className="text-[10px] font-mono uppercase tracking-widest px-1.5"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            initial={{ opacity: 0 }}
            animate={phase === 'done' ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 + i * 0.06 }}>
            {w}
          </motion.span>
        ))}
      </div>
    </div>
  );
}
